import React from "react"
import type { UVInsertConfig } from "@shared/models/uv-inserts"
import type { SuggestedMessaging } from "@shared/models/uv-inserts/suggested-messaging"
import { toSuggestionChooserLabel, toSuggestionInsertText } from "@shared/view/uv-inserts/suggestion-text"
import { Chooser } from "../Chooser"
import chooserStyles from "../Chooser/chooser.module.css"
import { useUvInsertChooserViewModel } from "./UvInsertChooser.vm"
import styles from "./uv-insert-chooser.module.css"

export type UvInsertChooserProps = {
  config: UVInsertConfig
  /** Active visualiser region tab (same index as FaceTabs). */
  tabIndex: number
  /** Parsed suggestion groups (e.g. from `resolveUvInserts` or bootstrap). */
  suggestedMessaging: SuggestedMessaging
  selectedThemeIndex: number
  onThemeIndexChange: (index: number) => void
  themeChooserOpen: boolean
  onThemeChooserOpenChange: (open: boolean) => void
  selectedSuggestionGroupIndex: number
  onSelectedSuggestionGroupIndexChange: (index: number) => void
  suggestionChooserOpen: boolean
  onSuggestionChooserOpenChange: (open: boolean) => void
  /** Fired when the user picks a suggestion line (apply to your text state). */
  onSuggestionPick: (text: string) => void
  className?: string
}

/**
 * Theme + suggested-message panels driven by shared `getUvInsertChooserPlan`. Renders nothing when
 * the current tab/config has neither multi-theme nor matching suggestions.
 */
export function UvInsertChooser({
  config,
  tabIndex,
  suggestedMessaging,
  selectedThemeIndex,
  onThemeIndexChange,
  themeChooserOpen,
  onThemeChooserOpenChange,
  selectedSuggestionGroupIndex,
  onSelectedSuggestionGroupIndexChange,
  suggestionChooserOpen,
  onSuggestionChooserOpenChange,
  onSuggestionPick,
  className = "",
}: UvInsertChooserProps) {
  const vm = useUvInsertChooserViewModel({
    config,
    tabIndex,
    suggestedMessaging,
    selectedThemeIndex,
    selectedSuggestionGroupIndex,
  })

  if (!vm.plan.visible) {
    return null
  }

  return (
    <div className={`uv-inserts-chooser-strip ${className}`.trim()}>
      {vm.themeItems ? (
        <Chooser
          title="Choose a themed insert design"
          subtitle={vm.themeSubtitle}
          open={themeChooserOpen}
          onToggle={onThemeChooserOpenChange}
        >
          <div className={chooserStyles["stock-card--chooser-grid"]}>
            {vm.themeItems.map((item) => (
              <button
                key={item.variantId}
                type="button"
                className={`${chooserStyles["stock-card--chooser-item"]} ${
                  vm.safeThemeIndex === item.index
                    ? chooserStyles["stock-card--chooser-item--selected"]
                    : ""
                }`}
                onClick={() => onThemeIndexChange(item.index)}
              >
                {item.imageSrc ? <img src={item.imageSrc} alt="" /> : null}
                <p>{item.title.replace(/^Text\s*\/\s*/i, "").trim() || item.title}</p>
              </button>
            ))}
          </div>
        </Chooser>
      ) : null}

      {vm.groups.length > 0 ? (
        <Chooser
          title="Suggested Messages"
          subtitle={vm.suggestionSubtitle}
          open={suggestionChooserOpen}
          onToggle={onSuggestionChooserOpenChange}
        >
          <div className={styles.suggestions}>
            <div className={styles.suggestions__column}>
              {vm.groups.map((group, index) => (
                <div
                  key={group.key}
                  className={`${styles.suggestions__item} ${
                    vm.safeGroupIndex === index ? styles["suggestions__item--selected"] : ""
                  }`}
                  onClick={() => onSelectedSuggestionGroupIndexChange(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      onSelectedSuggestionGroupIndexChange(index)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <p>{group.name}</p>
                </div>
              ))}
            </div>
            <div className={styles.suggestions__column} style={{ marginLeft: 20 }}>
              <ul className={styles.suggestions__list}>
                {vm.activeSuggestions.map((text, index) => (
                  <li key={`${index}-${text}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onSuggestionPick(toSuggestionInsertText({ text }))
                        onSuggestionChooserOpenChange(false)
                      }}
                    >
                      {toSuggestionChooserLabel({ text })}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Chooser>
      ) : null}
    </div>
  )
}
